import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Image as ImageIcon,
  ReceiptLong as ReceiptLongIcon,
  Storefront as StorefrontIcon,
} from '@mui/icons-material';
import { fetchProductSales } from '../api/productSales';
import type {
  ProductSaleRow,
  ProductSalesListQuery,
  TransactionStatus,
  TransactionType,
} from '../types/productSales';
import {
  TRANSACTION_STATUS_LABELS,
  TRANSACTION_STATUS_OPTIONS,
  TRANSACTION_TYPE_LABELS,
  TRANSACTION_TYPE_OPTIONS,
} from '../types/productSales';

// ─── Constants ────────────────────────────────────────────────────────────────

const CARD_BORDER = '1px solid';
const CARD_BORDER_COLOR = 'rgba(226, 232, 240, 1)';
const TABLE_HEADER_BG = 'rgba(248, 250, 252, 1)';

// ─── Formatters ───────────────────────────────────────────────────────────────

const DATE_FMT = new Intl.DateTimeFormat('es-MX', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

function formatDate(iso: string): string {
  try {
    return DATE_FMT.format(new Date(iso));
  } catch {
    return '—';
  }
}

// ─── Status chip ──────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<TransactionStatus, { bg: string; color: string }> = {
  COMPLETED: { bg: '#F0FDF4', color: '#15803D' },
  PENDING: { bg: '#FEFCE8', color: '#A16207' },
  PARTIAL: { bg: '#EFF6FF', color: '#1D4ED8' },
  CANCELLED: { bg: '#F1F5F9', color: '#64748B' },
};

function StatusChip({ status }: { status: TransactionStatus }): React.ReactElement {
  const style = STATUS_STYLES[status] ?? { bg: '#F1F5F9', color: '#64748B' };
  return (
    <Chip
      label={TRANSACTION_STATUS_LABELS[status] ?? status}
      size="small"
      sx={{
        bgcolor: style.bg,
        color: style.color,
        fontWeight: 700,
        fontSize: 10,
        height: 22,
        letterSpacing: 0.3,
        borderRadius: '9999px',
      }}
    />
  );
}

// ─── Type chip ────────────────────────────────────────────────────────────────

function TypeChip({ type }: { type: TransactionType }): React.ReactElement {
  const isSale = type === 'SALE';
  return (
    <Chip
      label={TRANSACTION_TYPE_LABELS[type] ?? type}
      size="small"
      sx={{
        bgcolor: isSale ? '#F5F3FF' : '#FFF7ED',
        color: isSale ? '#6D28D9' : '#C2410C',
        fontWeight: 700,
        fontSize: 10,
        height: 22,
        letterSpacing: 0.3,
        borderRadius: '9999px',
      }}
    />
  );
}

// ─── Channel chip ─────────────────────────────────────────────────────────────

function ChannelChip({ channel }: { channel: ProductSaleRow['channel'] }): React.ReactElement {
  if (!channel) {
    return (
      <Typography variant="caption" color="text.disabled">
        —
      </Typography>
    );
  }
  const isShopify = channel === 'shopify';
  return (
    <Chip
      icon={isShopify ? <StorefrontIcon sx={{ fontSize: '12px !important' }} /> : undefined}
      label={isShopify ? 'Shopify' : 'Tienda'}
      size="small"
      sx={{
        bgcolor: isShopify ? '#F0FDF4' : '#F5F3FF',
        color: isShopify ? '#15803D' : '#6D28D9',
        fontWeight: 700,
        fontSize: 10,
        height: 22,
        borderRadius: '9999px',
        '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
      }}
    />
  );
}

// ─── Default filters ──────────────────────────────────────────────────────────

const defaultFilters = {
  productName: '',
  status: '' as TransactionStatus | '',
  type: '' as TransactionType | '',
  shopifySaleId: '',
  variantSearch: '',
};

type FiltersState = typeof defaultFilters;

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AdminProductSalesPage(): React.ReactElement {
  const [draftFilters, setDraftFilters] = useState<FiltersState>(defaultFilters);
  const [committedFilters, setCommittedFilters] = useState<FiltersState>(defaultFilters);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rows, setRows] = useState<ProductSaleRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const buildQuery = useCallback((): ProductSalesListQuery => {
    return {
      page: page + 1,
      pageSize,
      productName: committedFilters.productName.trim() || undefined,
      status: committedFilters.status || undefined,
      type: committedFilters.type || undefined,
      shopifySaleId: committedFilters.shopifySaleId.trim() || undefined,
      variantSearch: committedFilters.variantSearch.trim() || undefined,
    };
  }, [page, pageSize, committedFilters]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const result = await fetchProductSales(buildQuery());
    setLoading(false);
    if (!result.ok) {
      setLoadError(result.error);
      setRows([]);
      setTotal(0);
      return;
    }
    setRows(result.data.items);
    setTotal(result.data.total);
  }, [buildQuery]);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const handleApplyFilters = () => {
    setCommittedFilters({ ...draftFilters });
    setPage(0);
  };

  const handleClearFilters = () => {
    setDraftFilters(defaultFilters);
    setCommittedFilters(defaultFilters);
    setPage(0);
    setPageSize(25);
  };

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(e.target.value, 10));
    setPage(0);
  };

  const updateDraft = <K extends keyof FiltersState>(key: K, value: FiltersState[K]) =>
    setDraftFilters((prev) => ({ ...prev, [key]: value }));

  const onStatusChange = (e: SelectChangeEvent<string>) =>
    updateDraft('status', e.target.value as TransactionStatus | '');

  const onTypeChange = (e: SelectChangeEvent<string>) =>
    updateDraft('type', e.target.value as TransactionType | '');

  const rangeLabel =
    total === 0 ? '0' : `${page * pageSize + 1}–${Math.min((page + 1) * pageSize, total)}`;

  const hasActiveFilters = Object.values(committedFilters).some((v) => v !== '');

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, letterSpacing: '-0.02em', textAlign: 'left' }}
          >
            Ventas
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, textAlign: 'left' }}>
            Historial de transacciones de productos — ordenado por fecha, más reciente primero.
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          border: CARD_BORDER,
          borderColor: CARD_BORDER_COLOR,
          borderRadius: 2,
          overflow: 'hidden',
          mb: 3,
        }}
      >
        <Box
          sx={{
            p: 2,
            borderBottom: CARD_BORDER,
            borderColor: CARD_BORDER_COLOR,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
            Filtros
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(5, 1fr)',
              },
              gap: 2,
            }}
          >
            {/* Product name */}
            <TextField
              size="small"
              label="Nombre del producto"
              placeholder="Buscar por título…"
              value={draftFilters.productName}
              onChange={(e) => updateDraft('productName', e.target.value)}
            />

            {/* Art ID / Variant ID */}
            <TextField
              size="small"
              label="Art ID o ID de inventario"
              placeholder="UU-00042 o 1001"
              value={draftFilters.variantSearch}
              onChange={(e) => updateDraft('variantSearch', e.target.value)}
              InputProps={{ sx: { fontFamily: 'ui-monospace, monospace' } }}
            />

            {/* Shopify order ID */}
            <TextField
              size="small"
              label="ID de orden Shopify"
              placeholder="ID parcial o completo"
              value={draftFilters.shopifySaleId}
              onChange={(e) => updateDraft('shopifySaleId', e.target.value)}
              InputProps={{ sx: { fontFamily: 'ui-monospace, monospace' } }}
            />

            {/* Status */}
            <FormControl size="small">
              <InputLabel id="sales-status-label">Estado</InputLabel>
              <Select
                labelId="sales-status-label"
                label="Estado"
                value={draftFilters.status}
                onChange={onStatusChange}
              >
                <MenuItem value="">
                  <em>Todos</em>
                </MenuItem>
                {TRANSACTION_STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {TRANSACTION_STATUS_LABELS[s]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Type */}
            <FormControl size="small">
              <InputLabel id="sales-type-label">Tipo</InputLabel>
              <Select
                labelId="sales-type-label"
                label="Tipo"
                value={draftFilters.type}
                onChange={onTypeChange}
              >
                <MenuItem value="">
                  <em>Todos</em>
                </MenuItem>
                {TRANSACTION_TYPE_OPTIONS.map((t) => (
                  <MenuItem key={t} value={t}>
                    {TRANSACTION_TYPE_LABELS[t]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Button
              variant="contained"
              onClick={handleApplyFilters}
              sx={{
                bgcolor: '#000',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                '&:hover': { bgcolor: '#1e293b' },
              }}
            >
              Aplicar filtros
            </Button>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              disabled={!hasActiveFilters}
              sx={{
                borderColor: CARD_BORDER_COLOR,
                color: 'text.primary',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
              }}
            >
              Limpiar filtros
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary">
            Mostrando {rangeLabel} de {total} transacciones
          </Typography>
        </Box>

        {/* Error */}
        {loadError && (
          <Alert severity="warning" sx={{ m: 2, mb: 0 }}>
            {loadError}
          </Alert>
        )}

        {/* Table */}
        <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 1100 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: TABLE_HEADER_BG }}>
                {[
                  'ID transacción',
                  'Tipo',
                  'Fecha',
                  'Producto',
                  'Art ID / Inv. ID',
                  'Estado',
                  'Canal',
                  'Orden Shopify',
                ].map((col) => (
                  <TableCell
                    key={col}
                    sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}
                  >
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading &&
                rows.map((row) => (
                  <TableRow
                    key={row.transactionId}
                    hover
                    sx={{ '&:last-child td': { borderBottom: 0 }, fontSize: 14 }}
                  >
                    {/* Transaction ID */}
                    <TableCell sx={{ color: 'text.secondary' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <ReceiptLongIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
                        >
                          {row.transactionId}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Type */}
                    <TableCell>
                      <TypeChip type={row.type} />
                    </TableCell>

                    {/* Date */}
                    <TableCell
                      sx={{ whiteSpace: 'nowrap', color: 'text.secondary', minWidth: 150 }}
                    >
                      {formatDate(row.transactionCreatedAt)}
                    </TableCell>

                    {/* Product + Variant combined */}
                    <TableCell sx={{ minWidth: 240 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 1,
                            bgcolor: 'action.hover',
                            border: CARD_BORDER,
                            borderColor: CARD_BORDER_COLOR,
                            overflow: 'hidden',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {row.imageUrl ? (
                            <Box
                              component="img"
                              src={row.imageUrl}
                              alt={row.productName || 'Imagen del producto'}
                              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <ImageIcon sx={{ color: 'action.disabled' }} />
                          )}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {row.productName || 'Sin título'}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.25 }}
                            noWrap
                          >
                            {row.variantName || '—'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Art ID / Inventory ID */}
                    <TableCell sx={{ minWidth: 140 }}>
                      {row.artId ? (
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'ui-monospace, monospace',
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          UU{row.artId}
                        </Typography>
                      ) : (
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          sx={{ fontStyle: 'italic' }}
                        >
                          Sin Art ID
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ display: 'block', fontFamily: 'ui-monospace, monospace' }}
                      >
                        #{row.variantId}
                      </Typography>
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <StatusChip status={row.status} />
                    </TableCell>

                    {/* Channel */}
                    <TableCell>
                      <ChannelChip channel={row.channel} />
                    </TableCell>

                    {/* Shopify order ID */}
                    <TableCell sx={{ minWidth: 140 }}>
                      {row.shopifySaleId ? (
                        <Tooltip title="Ver orden en Shopify Admin">
                          <Typography
                            component="a"
                            href={`https://admin.shopify.com/store/urban-utility-5183/orders/${row.shopifySaleId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="body2"
                            sx={{
                              fontFamily: 'ui-monospace, monospace',
                              fontSize: 12,
                              maxWidth: 130,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block',
                              color: '#1D4ED8',
                              textDecoration: 'none',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {row.shopifySaleId}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}

              {loading && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={28} sx={{ color: '#111827' }} />
                  </TableCell>
                </TableRow>
              )}

              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <ReceiptLongIcon sx={{ fontSize: 40, color: 'grey.300' }} />
                      <Typography color="text.secondary" fontWeight={600}>
                        Sin transacciones
                      </Typography>
                      {hasActiveFilters && (
                        <Typography variant="body2" color="text.disabled">
                          Ninguna transacción coincide con los filtros actuales.
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Filas por página"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      </Paper>
    </Box>
  );
}
